import React from 'react';

import ModeIcon from '@mui/icons-material/Mode';

import { ClickAwayListener, IconButton } from '@mui/material';

import styled from 'styled-components';

import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';

import CloseIcon from '@mui/icons-material/Close';

import CustomTextInputField from 'components/SharedFormComponents/CustomTextInputField';

import API from 'utils/api';

import { useStore } from 'utils/hooks/store';

import { colors } from 'theme/colors';
import { PipelineDetailsType } from 'stores/PipelineDetails/PipelineDetails';
import { EntityModules } from 'core/constants';
import Logger from 'utils/logger';

export const HeaderTitle = styled.h1`
  color: ${({ theme }) => theme.palette.text.primary};
  font-size: 24px;
  font-weight: 500;
  letter-spacing: 0.15px;
  margin: 0;
  margin-top: 2px;
  display: flex;
  align-items: center;
  gap: 8px;

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const HeaderTitleInput = styled.div`
  margin-top: 2px;

  display: flex;
  align-items: center;

  & .MuiInputBase-input {
    font-size: 24px;
    font-weight: 500;
    padding: 6px 12px;
  }
`;

const EditableHeader = ({ pipeline }: { pipeline: PipelineDetailsType | undefined }): React.ReactElement => {
  if (!pipeline) return <></>;
  const { name, uuid } = pipeline;
  const { notification } = useStore();

  const [editMode, setEditMode] = React.useState<boolean>(false);
  const [pipelineName, setPipelineName] = React.useState<string>(name);

  const handleClickAway = async (): Promise<void> => {
    if (pipelineName.length > 255) {
      notification.enqueueErrorSnackbar('Pipeline name must be 255 characters or less');
      return;
    }
    if (pipelineName === '') {
      notification.enqueueErrorSnackbar('Pipeline name cannot be empty');
      return;
    }

    try {
      await API.entities.updatePipeline(EntityModules.Events, uuid, { name: pipelineName })();
      setEditMode(false);
      setPipelineName(pipelineName);
      notification.enqueueSuccessSnackbar('Pipeline name updated');
    } catch (error) {
      notification.enqueueErrorSnackbar('Failed to update pipeline name');
      Logger.error(error as string);
    }
  };

  const handleEditClick = (): void => {
    setEditMode(true);
  };

  const handleDiscardChanges = (): void => {
    setEditMode(false);
    setPipelineName(name);
  };

  if (editMode) {
    return (
      <ClickAwayListener onClickAway={handleClickAway} key={uuid}>
        <HeaderTitleInput>
          <CustomTextInputField
            onChange={(e) => setPipelineName(e as string)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleClickAway();
            }}
            description={pipelineName}
            value={pipelineName}
          />
          <IconButton onClick={handleDiscardChanges} data-testid="discardButton">
            <CloseIcon sx={{ width: '16px', height: '16px' }} htmlColor={colors.text.secondary} />
          </IconButton>
          <IconButton onClick={handleClickAway} data-testid="saveButton">
            <CheckOutlinedIcon sx={{ width: '16px', height: '16px' }} htmlColor={colors.text.secondary} />
          </IconButton>
        </HeaderTitleInput>
      </ClickAwayListener>
    );
  }

  return (
    <HeaderTitle key={uuid}>
      {pipelineName}
      <ModeIcon onClick={handleEditClick} sx={{ color: colors.text.secondary, cursor: 'pointer' }} />
    </HeaderTitle>
  );
};

export default EditableHeader;
